import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Link } from "react-router";
import type { PublicBanner } from "../../shared/banners";
import { useI18n } from "../i18n/I18nProvider";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { BannerSlide } from "./BannerSlide";
import { HeroCards } from "./HeroCards";

/** Seconds each banner stays on screen. */
const AUTOPLAY_MS = 7000;
const SWIPE_PX = 40;

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/** The home banner before the carousel loads (or when no banner is live): the store's own pitch. */
function DefaultHero() {
  const { t } = useI18n();
  return (
    <section className="hero">
      <div className="container hero-inner">
        <div>
          <h1>{t("home.heroTitle")}</h1>
          <p>{t("home.heroText")}</p>
          <Link to="/mercado" className="btn btn-accent btn-lg">
            {t("closed.heroCta")}
          </Link>
        </div>
        <HeroCards variant="hero" />
      </div>
    </section>
  );
}

/**
 * The home page banners as a carousel (Admin › Banners): advances by itself every few seconds,
 * pauses while hovered or focused (and never moves on its own with reduced motion), and can be
 * driven with the arrows, the dots, the keyboard or a swipe.
 */
export function HeroCarousel() {
  const result = useAsync((signal) => api.banners(signal), []);
  const banners = result.data ?? [];
  if (!banners.length) return <DefaultHero />;
  return <Carousel banners={banners} />;
}

export function Carousel({ banners }: { banners: PublicBanner[] }) {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [stopped, setStopped] = useState(reducedMotion);
  const dragX = useRef<number | null>(null);
  const count = banners.length;
  const go = useCallback((n: number) => setIndex(((n % count) + count) % count), [count]);

  useEffect(() => {
    if (count < 2 || paused || stopped) return;
    // Keeps ticking while the tab is hidden, but only moves when someone can see it.
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") go(index + 1);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [index, paused, stopped, count, go]);

  // Fewer banners after a reload (one expired): stay in range.
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [index, count]);

  if (count === 1) return <BannerSlide banner={banners[0]} first />;

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight") go(index + 1);
    else if (e.key === "ArrowLeft") go(index - 1);
    else return;
    e.preventDefault();
  };
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") dragX.current = e.clientX;
  };
  const onPointerUp = (e: PointerEvent) => {
    if (dragX.current === null) return;
    const dx = e.clientX - dragX.current;
    dragX.current = null;
    if (Math.abs(dx) > SWIPE_PX) go(index + (dx < 0 ? 1 : -1));
  };

  return (
    <section
      className="carousel"
      aria-roledescription="carousel"
      aria-label={t("banner.label")}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
      onKeyDown={onKey}
    >
      <div className="carousel-viewport" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => (dragX.current = null)}>
        <div className="carousel-track" style={{ transform: `translateX(-${index * 100}%)` }} aria-live={paused || stopped ? "polite" : "off"}>
          {banners.map((b, i) => (
            <div
              key={b.id}
              className="carousel-slide"
              role="group"
              aria-roledescription="slide"
              aria-label={t("banner.slide", { n: i + 1, total: count })}
              aria-hidden={i !== index}
              inert={i !== index ? true : undefined}
            >
              <BannerSlide banner={b} active={i === index} first={i === 0} />
            </div>
          ))}
        </div>
      </div>
      <div className="carousel-controls">
        <button type="button" className="carousel-arrow" onClick={() => go(index - 1)} aria-label={t("banner.prev")}>
          ‹
        </button>
        <div className="carousel-dots">
          {banners.map((b, i) => (
            <button
              key={b.id}
              type="button"
              className={i === index ? "carousel-dot is-active" : "carousel-dot"}
              aria-label={t("banner.goTo", { n: i + 1 })}
              aria-current={i === index ? "true" : undefined}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button type="button" className="carousel-arrow" onClick={() => go(index + 1)} aria-label={t("banner.next")}>
          ›
        </button>
        <button type="button" className="carousel-toggle" onClick={() => setStopped((s) => !s)} aria-label={stopped ? t("banner.play") : t("banner.pause")}>
          {stopped ? "▶" : "❚❚"}
        </button>
      </div>
    </section>
  );
}
